import {
  QBox,
  QButton,
  QStack,
  QText,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';
import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentFormat } from '../../../api/model/document';
import { DocumentOverviewContext } from '../DocumentOverview/Context';
import { useGenerateTrainingAssessment } from './hooks/useGenerateTrainingAssessment';
import { SuggestionDrawer } from './SuggestionDrawer';
import { TrainingAssessmentAIResponse } from './types/trainingAssessment';

type Props = {
  onAccept: (questions: TrainingAssessmentAIResponse['questions']) => void;
};

export const GenerateTrainingAssessmentAI: React.FC<Props> = ({ onAccept }) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const aiEnabled = currentUser.company.ai_enabled;
  const { qualioDocument } = useContext(DocumentOverviewContext);
  const { isLoading, mutate: generateTrainingAssessment } =
    useGenerateTrainingAssessment();
  const [isOpen, setIsOpen] = useState(false);
  const [suggestion, setSuggestion] =
    useState<TrainingAssessmentAIResponse | null>(null);
  const [accepted, setAccepted] = useState(false);
  const { showToast } = useToastProvider();

  const generateClick = useCallback(() => {
    setIsOpen(true);
    setAccepted(false);
    generateTrainingAssessment(undefined, {
      onSuccess: (assessment: TrainingAssessmentAIResponse) => {
        setSuggestion(assessment);
      },
      onError: (error: any) => {
        // Close the drawer and clear any prior suggestion so the loading
        // spinner stops and a stale (now-invalid) suggestion cannot be viewed
        // or accepted. Without this the drawer is left in a misleading empty
        // state after a failed generation (PS-235).
        setIsOpen(false);
        setSuggestion(null);
        setAccepted(false);

        const status = error?.response?.status;
        // PS-238: a 422 means the document content is junk / placeholder /
        // blank, so the backend declined to generate.
        const isInsufficientContent = status === 422;
        // PS-235: a 413 (CONTEXT_WINDOW_EXCEEDED) means the document content
        // is too large to fit the model's context window.
        const isContentTooLarge = status === 413;
        // A 503 (MODEL_UNAVAILABLE) means Bedrock is transiently out of
        // capacity. This is not the user's fault and is retryable, so
        // show a "temporarily unavailable" message. The banner's Generate
        // button acts as the retry once the drawer has closed.
        const isServiceUnavailable =
          status === 503 || error?.response?.data?.code === 'MODEL_UNAVAILABLE';

        let dataCy = 'generate-training-assessment-error-toast';
        let toastStatus: 'warning' | 'error' = 'error';
        let description = t(
          'errors.failedToCreateTrainingAssessmentSuggestions',
        );

        if (isInsufficientContent) {
          dataCy = 'generate-training-assessment-insufficient-content-toast';
          toastStatus = 'warning';
          description =
            error?.response?.data?.reason ??
            t('errors.insufficientDocumentContent');
        } else if (isContentTooLarge) {
          dataCy = 'generate-training-assessment-content-too-large-toast';
          toastStatus = 'warning';
          description =
            error?.response?.data?.message ??
            t('errors.documentTooLargeToGenerate');
        } else if (isServiceUnavailable) {
          dataCy = 'generate-training-assessment-service-unavailable-toast';
          toastStatus = 'warning';
          description =
            error?.response?.data?.message ??
            t('errors.aiTemporarilyUnavailable');
        }

        showToast({
          'data-cy': dataCy,
          id: 'generate-training-assessment-error',
          status: toastStatus,
          title: t('notifications.cannotGenerateTrainingAssessment'),
          description,
          replace: true,
        });
      },
    });
  }, [generateTrainingAssessment, showToast, t]);

  const onAcceptCallback = useCallback(() => {
    if (!suggestion?.rationale || accepted) {
      return;
    }

    setAccepted(true);
    setIsOpen(false);
    onAccept(suggestion.questions);
    showToast({
      'data-cy': 'generate-training-assessment-ai-notify-success-toast',
      status: 'success',
      title: t('notifications.suggestionsAccepted'),
      description: t('messages.suggestedQuestionsAddedToEnd'),
      duration: 5000,
    });
  }, [suggestion, onAccept, accepted, showToast, t]);

  if (
    !aiEnabled ||
    qualioDocument.document_format === DocumentFormat.DocumentUpload
  ) {
    return null;
  }

  return (
    <>
      <SuggestionDrawer
        isLoading={isLoading}
        isOpen={isOpen}
        accepted={accepted}
        onGenerate={generateClick}
        onAccept={onAcceptCallback}
        suggestion={suggestion}
        onClose={() => setIsOpen(false)}
      />
      <QBox
        width="full"
        borderRadius="md"
        padding="16px 24px"
        background="linear-gradient(90.14deg, #3D4CF2 0%, #7B4FFF 100%)"
        data-cy="generate-banner"
      >
        <QStack
          justify="space-between"
          alignContent="center"
          alignItems="center"
          direction="row"
        >
          <QStack direction="column">
            <QText weight="semibold" color="white" fontSize="md">
              {t('labels.needSuggestions')}
            </QText>
            <QText fontSize="xs" color="white">
              {t('messages.useQualioAiForSuggestions')}
            </QText>
          </QStack>
          {suggestion !== null ? (
            <QStack direction="row">
              <QButton
                data-cy="regenerate"
                onClick={generateClick}
                isDisabled={isLoading}
                leftIcon="QualioAI"
                variant="outline"
              >
                {t('buttons.regenerate')}
              </QButton>
              <QButton
                data-cy="view-suggestions"
                variant="outline"
                onClick={() => setIsOpen(true)}
                isDisabled={isLoading}
              >
                {t('buttons.viewSuggestions')}
              </QButton>
            </QStack>
          ) : (
            <QButton
              data-cy="generate-ai-ta"
              variant="outline"
              onClick={generateClick}
              isDisabled={isLoading}
              leftIcon="QualioAI"
            >
              {t('buttons.generate')}
            </QButton>
          )}
        </QStack>
      </QBox>
    </>
  );
};
